import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Copy, CheckCircle2, AlertTriangle, KeyRound } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import {
  listProvisioningTokens,
  createProvisioningToken,
  revokeProvisioningToken,
  type Platform
} from '@/api/fleet';
import { timeAgo } from '@/lib/format';

export function TokensScreen() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [rawToken, setRawToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: tokens, isLoading } = useQuery({
    queryKey: ['fleet-tokens'],
    queryFn: listProvisioningTokens
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeProvisioningToken(id),
    onSuccess: () => {
      toast.success('Token revoked');
      qc.invalidateQueries({ queryKey: ['fleet-tokens'] });
    }
  });

  const copy = async (s: string) => {
    await navigator.clipboard.writeText(s);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="bottom-right" richColors />

      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-nurev-text">Provisioning Tokens</h1>
          <p className="mt-1 max-w-2xl text-sm text-nurev-muted">
            Embed one of these as <code className="rounded bg-nurev-card px-1.5 py-0.5 text-[11px]">NUREV_PROVISIONING_TOKEN</code>
            {' '}on each new server. The agent uses it on first boot to register
            itself + receive its own per-node bearer. Tokens themselves never
            authorize any data access.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary">
          <Plus className="h-4 w-4" /> Mint token
        </button>
      </header>

      {/* RAW-TOKEN-ONCE banner */}
      {rawToken && (
        <div className="rounded-xl border border-amber-500/50 bg-amber-500/5 p-5">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-300" />
            <h2 className="text-sm font-semibold uppercase tracking-wide text-amber-300">
              New token — copy now, this won't be shown again
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <code className="flex-1 select-all break-all rounded-lg border border-nurev-border bg-nurev-surface px-3 py-2 font-mono text-xs text-nurev-text">
              {rawToken}
            </code>
            <button onClick={() => copy(rawToken)} className="btn-secondary">
              {copied ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-emerald-300" /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" /> Copy
                </>
              )}
            </button>
          </div>
          <p className="mt-3 text-xs text-nurev-muted">
            Once you've embedded this in your provisioning automation, dismiss
            this card. The token itself isn't recoverable — only the suffix
            shown in the list below.
          </p>
          <button
            onClick={() => setRawToken(null)}
            className="btn-ghost mt-3 text-xs"
          >
            I've saved it. Dismiss.
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-3 text-nurev-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading tokens…
        </div>
      ) : !tokens || tokens.length === 0 ? (
        <div className="card text-center">
          <KeyRound className="mx-auto mb-3 h-8 w-8 text-nurev-muted" />
          <p className="text-sm text-nurev-muted">
            No provisioning tokens yet. Mint one to start onboarding nodes.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border bg-white/[0.02] text-left text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
                <th className="px-4 py-3">Label</th>
                <th className="px-4 py-3">Suffix</th>
                <th className="px-4 py-3">Default group</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Last used</th>
                <th className="w-24 px-4 py-3 text-right">Status</th>
                <th className="w-24 px-4 py-3 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map(t => {
                const revoked = !!t.revokedAt;
                return (
                  <tr
                    key={t._id}
                    className="border-b border-nurev-border/60 hover:bg-white/[0.02]"
                  >
                    <td className="px-4 py-3 font-medium text-nurev-text">{t.label}</td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-nurev-card px-2 py-0.5 font-mono text-[11px] text-nurev-text">
                        nrft_…{t.tokenSuffix}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-nurev-text-soft">
                      {t.defaultGroup || '—'}
                    </td>
                    <td className="px-4 py-3 text-nurev-muted">
                      {timeAgo(t.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-nurev-muted">
                      {timeAgo(t.lastUsedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {revoked ? (
                        <span className="rounded bg-red-500/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-red-300">
                          Revoked
                        </span>
                      ) : (
                        <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-300">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!revoked && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Revoke token "${t.label}"?`)) {
                              revoke.mutate(t._id);
                            }
                          }}
                          className="text-xs text-red-300 hover:underline"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateTokenModal
          onClose={() => setCreating(false)}
          onSuccess={raw => {
            setRawToken(raw);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['fleet-tokens'] });
          }}
        />
      )}
    </div>
  );
}

function CreateTokenModal({
  onClose,
  onSuccess
}: {
  onClose: () => void;
  onSuccess: (rawToken: string) => void;
}) {
  const [label, setLabel] = useState('');
  const [defaultGroup, setDefaultGroup] = useState('');
  const [defaultPlatform, setDefaultPlatform] = useState<Platform>('linux');

  const mut = useMutation({
    mutationFn: () =>
      createProvisioningToken({
        label,
        defaultGroup: defaultGroup || undefined,
        defaultPlatform
      }),
    onSuccess: r => onSuccess(r.provisioningToken)
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!label) return;
    mut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-4 rounded-2xl border border-emerald-500/30 bg-nurev-surface p-6"
      >
        <h2 className="text-lg font-bold text-nurev-text">Mint provisioning token</h2>

        <div>
          <label className="label">Label</label>
          <input
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="us-east-prod"
            className="input"
            required
          />
          <p className="mt-1 text-xs text-nurev-muted">
            Internal-only name. Helps you identify the token in the list later.
          </p>
        </div>

        <div>
          <label className="label">Default group (optional)</label>
          <input
            value={defaultGroup}
            onChange={e => setDefaultGroup(e.target.value)}
            placeholder="us-east-bm"
            className="input"
          />
          <p className="mt-1 text-xs text-nurev-muted">
            Nodes registered with this token land in this group by default.
          </p>
        </div>

        <div>
          <label className="label">Default platform</label>
          <select
            value={defaultPlatform}
            onChange={e => setDefaultPlatform(e.target.value as Platform)}
            className="input"
          >
            <option value="linux">Linux</option>
            <option value="windows">Windows</option>
            <option value="mac">macOS</option>
          </select>
        </div>

        {mut.error && (
          <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {(mut.error as Error).message}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={mut.isPending} className="btn-primary">
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Mint token'}
          </button>
        </div>
      </form>
    </div>
  );
}
